import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { deepEqual } from 'fast-equals';
import cn from 'classnames';

import useMartyContext from 'hooks/useMartyContext';
import useFocusTrap from 'hooks/useFocusTrap';
import { onEvent } from 'helpers/EventHelpers';
import { sortStringToObject } from 'helpers/SearchUtils';

import { collapsed, facetGroup, facetGroupHead, facetGroupName, facetList } from 'styles/components/search/newFacets.scss';
import css from 'styles/components/search/newSort.scss';

const newSearchId = 'searchSort';
export const NewSort = ({ compact, className, sortOptions, selectedSort, onSortSelected, makePersonalizedSortToggle, filters: { bestForYouSortEligible }, testId: sortTestId }) => {
  const [sortDropdownVisible, setSortDropdownVisible] = useState(false);
  const { testId } = useMartyContext();

  const focusRef = useFocusTrap({ active: sortDropdownVisible, shouldFocusFirstElement: true });

  const closeDropDown = () => {
    setSortDropdownVisible(false);
  };

  useEffect(() => {
    if (!compact) {

      onEvent(document.body, 'click', e => {
        if (sortDropdownVisible && e.target.id !== newSearchId) {
          closeDropDown();
        }
      }, this);

      onEvent(document.body, 'keydown', e => {
        if (e.key === 'Escape' && sortDropdownVisible) {
          closeDropDown();
        }
      }, this);
    }
  }, [compact, sortDropdownVisible]);

  /**
  * Given a list of sort options, return the option which matches
  * the passed in criteria.
  * @param  {object[]} sortOptions array of sort options
  * @param  {object} criteria      object of sort criteria
  * @return {object}               matching sort option or undefined
  */
  const getSelectedSortValue = (sortOptions, criteria) => {
    const matched = (sortOptions || []).find(({ value }) => deepEqual(sortStringToObject(value), criteria));
    return matched || sortOptions[0];
  };

  const toggleDropdown = () => {
    const toggled = !sortDropdownVisible;
    setSortDropdownVisible(toggled);
  };

  const makeSort = () => {
    const newSortOptions = [ ...sortOptions ];
    const selectedValue = getSelectedSortValue(newSortOptions, selectedSort);

    if (compact) {
      return (
        <section className={cn(facetGroup, { [collapsed]: sortDropdownVisible })} data-test-id={testId(sortTestId)}>
          <h3 className={facetGroupName}>
            <button className={facetGroupHead} onClick={toggleDropdown} type="button">Sort</button>
          </h3>
          <div className={facetList}>{makeSortOptions(newSortOptions, selectedValue, !!bestForYouSortEligible)}</div>
        </section>
      );
    }

    return (
      <span data-test-id={testId(sortTestId)}>
        <label htmlFor={newSearchId}>Sort by:</label>
        <button
          id={newSearchId}
          aria-haspopup="true"
          type="button"
          data-test-id={testId('newSortToggle')}
          className={css.sortSelect}
          onClick={toggleDropdown}
          value={selectedValue.label}
          aria-expanded={!!sortDropdownVisible}
          aria-label={`Sort by ${selectedValue.label}`}>
          {selectedValue.label}
        </button>
        {makeSortOptions(newSortOptions, selectedValue, !!bestForYouSortEligible)}
      </span>
    );
  };

  /**
  * Given an array of sort options, render option elements
  * @param  {object[]} optionData options to render
  * @return {object[]}            rendered options
  */
  const makeSortOptions = (optionData, selectedValue, showBestForYouSort) => {
    const options = optionData.map(({ eventLabel, label, value, ariaLabel }) => {
      const isSelected = selectedValue.value === value;
      const isBfu = label === 'Best For You';
      if (!isBfu || showBestForYouSort) {
        const buttonMarkup = (
          <button
            type="button"
            className={cn(css.personalizedBtn, { [css.selected]: isSelected, [css.bfu]: isBfu && compact })}
            key={value}
            value={value}
            data-test-id={isSelected ? testId('selectedSortValue') : null}
            onClick={onSortSelected}
            aria-pressed={isSelected}
            aria-label={ariaLabel}
            data-label={eventLabel || label}>{label}</button>
        );
        return compact ? <li key={value}>{buttonMarkup} {isBfu && makePersonalizedSortToggle('bestForYouMessaging', false, true)}</li> : buttonMarkup;
      }
    });

    return compact ? <ul className={css.sortDropdown}>{options}</ul> : <div className={css.sortDropdown} ref={focusRef}>{options}</div>;
  };

  return (
    <div className={cn(css.sort, { [css.visible]: sortDropdownVisible, [className]: className })}>
      {makeSort()}
    </div>
  );
};

NewSort.propTypes = {
  onSortSelected: PropTypes.func.isRequired,
  sortOptions: PropTypes.array.isRequired,
  selectedSort: PropTypes.object.isRequired
};

export default NewSort;
